/**
 * Fluidité B4 (P5#18) — « Après réception », par lot : DOE (pièces attendues, reçu,
 * relance), retenue de garantie (caution, libération), décompte général (DGD, PDF).
 * Réservé à la maîtrise d'œuvre. API : backend/routes/fin_chantier_routes.py.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, FilePdf, PaperPlaneTilt, Plus, Trash, CaretDown, CaretRight } from '@phosphor-icons/react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { libelleRelance, toastEnvoi } from '../../../lib/envoiEmail';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const dateFr = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).length <= 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString('fr-FR');
};
const eur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function ApresReception({ chantierId, refreshKey }) {
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState({});

  const charger = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/apres-reception`, { withCredentials: true });
      setData(d);
    } catch {
      setData(null);
    }
  }, [chantierId]);

  useEffect(() => { charger(); }, [charger, refreshKey]);

  const lots = (data?.lots || []).filter((l) => l.receptionne);
  if (!data || lots.length === 0) return null;

  const telechargerDgd = async (lot) => {
    try {
      const res = await axios.get(`${API_URL}/api/chantiers/${chantierId}/dgd.pdf`,
        { params: lot ? { lot } : {}, withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DGD ${lot ? `lot ${lot}` : 'tous les lots'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Téléchargement impossible');
    }
  };

  const doe = async (lot, body) => {
    try {
      await axios.put(`${API_URL}/api/chantiers/${chantierId}/apres-reception/doe`, { lot, ...body }, { withCredentials: true });
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const relancerDoe = async (lot) => {
    try {
      const { data: r } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/apres-reception/doe/relancer`, { lot }, { withCredentials: true });
      toastEnvoi(toast, r);
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Relance impossible');
    }
  };

  const retenue = async (lot, body, msg) => {
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/apres-reception/retenue`, { lot, ...body }, { withCredentials: true });
      if (msg) toast.success(msg);
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="bg-white border border-[#E0E0DB] p-4 space-y-3" data-testid="reception-apres">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Package size={18} className="text-[#14B8A6]" />
          <h3 className="font-medium text-[#2C2C2C]">Après réception : DOE, retenue de garantie, décompte</h3>
        </div>
        <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => telechargerDgd(null)} data-testid="apres-dgd-tous">
          <FilePdf size={14} /> Décompte général (DGD)
        </Button>
      </div>
      <div className="border border-[#F0F0EB] divide-y divide-[#F0F0EB]">
        {lots.map((l) => {
          const deplie = !!ouvert[l.lot_code];
          return (
            <div key={l.lot_code} data-testid={`apres-lot-${l.lot_code}`}>
              <button type="button" onClick={() => setOuvert((o) => ({ ...o, [l.lot_code]: !deplie }))}
                className="w-full flex flex-wrap items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#FAFAF8]">
                {deplie ? <CaretDown size={14} /> : <CaretRight size={14} />}
                <span className="font-medium">Lot {l.lot_code}{l.lot_nom ? ` — ${l.lot_nom}` : ''}</span>
                <span className="text-[#6B6B6B]">{l.entreprise}</span>
                <span className="ml-auto flex flex-wrap gap-1">
                  <Badge className={`rounded-none border ${l.doe.complet ? 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]' : 'bg-[#FFF8E1] text-[#7A5A00] border-[#B8860B]'}`}>
                    DOE {l.doe.nb_recues}/{l.doe.nb_attendues}
                  </Badge>
                  <Badge className="rounded-none border bg-[#F5F5F3] text-[#4A4A4A] border-[#E0E0DB]">
                    {l.retenue.liberee_le ? 'Retenue libérée' : l.retenue.montant_ht > 0 ? `Retenue ${eur(l.retenue.montant_ht)}` : 'Pas de retenue'}
                  </Badge>
                </span>
              </button>
              {deplie && (
                <div className="px-3 pb-3 grid md:grid-cols-3 gap-4 text-sm">
                  <DoeLot lot={l} onDoe={doe} onRelancer={relancerDoe} />
                  <div className="space-y-2">
                    <p className="font-medium text-[#2C2C2C]">Retenue de garantie</p>
                    <p className="text-[#4A4A4A]">{l.retenue.montant_ht > 0 ? `${eur(l.retenue.montant_ht)} retenus sur les situations` : 'Aucune retenue sur les situations certifiées'}</p>
                    <p className="text-xs text-[#6B6B6B]">Réception du {dateFr(l.date_reception)} · GPA jusqu'au {dateFr(l.gpa_fin)}</p>
                    {l.retenue.liberee_le ? (
                      <p className="text-xs text-[#2D6A4F]">
                        Libérée le {dateFr(l.retenue.liberee_le)}{l.retenue.mode === 'caution' ? ' (sur caution)' : ' (fin de la GPA)'}{l.retenue.liberee_par_nom ? ` par ${l.retenue.liberee_par_nom}` : ''}
                        {' · '}<button type="button" className="underline" onClick={() => retenue(l.lot_code, { action: 'annuler' })}>annuler</button>
                      </p>
                    ) : (
                      <>
                        {l.retenue.caution
                          ? <p className="text-xs text-[#1E3A5F]">Caution reçue le {dateFr(l.retenue.caution_le)}{l.retenue.caution_ref ? ` (${l.retenue.caution_ref})` : ''}</p>
                          : (
                            <button type="button" className="text-xs underline text-[#1E3A5F]" data-testid={`apres-caution-${l.lot_code}`}
                              onClick={() => retenue(l.lot_code, { action: 'caution' }, 'Caution enregistrée')}>
                              L'entreprise a fourni une caution
                            </button>
                          )}
                        {l.retenue.motif && <p className="text-xs text-[#8B3A3A]">{l.retenue.motif}</p>}
                        <Button size="sm" className="rounded-none bg-[#2D6A4F] hover:bg-[#1B4332] h-8" disabled={!l.retenue.liberable}
                          onClick={() => retenue(l.lot_code, { action: 'liberer' }, 'Retenue libérée')} data-testid={`apres-liberer-${l.lot_code}`}>
                          Libérer la retenue
                        </Button>
                      </>
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium text-[#2C2C2C]">Décompte (HT)</p>
                    <Ligne libelle="Marché avec avenants" valeur={eur(l.dgd.marche_total_ht)} />
                    <Ligne libelle="Travaux certifiés" valeur={eur(l.dgd.travaux_certifies_ht)} />
                    <Ligne libelle="Déjà payé" valeur={eur(l.dgd.deja_paye_ht)} />
                    <Ligne libelle="Solde à régler" valeur={eur(l.dgd.reste_a_payer_ht)} fort />
                    <Button size="sm" variant="outline" className="rounded-none gap-1 h-8 mt-1" onClick={() => telechargerDgd(l.lot_code)}>
                      <FilePdf size={14} /> DGD du lot
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Ligne({ libelle, valeur, fort }) {
  return (
    <p className={`flex justify-between gap-2 ${fort ? 'font-medium text-[#2C2C2C]' : 'text-[#4A4A4A]'}`}>
      <span>{libelle}</span><span className="tabular-nums">{valeur}</span>
    </p>
  );
}

function DoeLot({ lot, onDoe, onRelancer }) {
  const [nouvelle, setNouvelle] = useState('');
  const derniere = libelleRelance((lot.doe.relances || []).slice(-1)[0], dateFr);
  return (
    <div className="space-y-2">
      <p className="font-medium text-[#2C2C2C]">DOE (dossier des ouvrages exécutés)</p>
      <ul className="space-y-1">
        {lot.doe.pieces.map((p) => (
          <li key={p.id} className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={!!p.recu} onChange={(e) => onDoe(lot.lot_code, { piece_id: p.id, recu: e.target.checked })}
              data-testid={`doe-${lot.lot_code}-${p.id}`} />
            <span className={`flex-1 ${p.recu ? 'text-[#2D6A4F]' : ''}`}>
              {p.libelle}{p.recu && p.recu_le ? <span className="text-xs text-[#6B6B6B]"> — reçu le {dateFr(p.recu_le)}</span> : ''}
            </span>
            <button type="button" aria-label="Retirer" className="text-[#9A9A9A] hover:text-[#8B3A3A]" onClick={() => onDoe(lot.lot_code, { retirer: p.id })}><Trash size={12} /></button>
          </li>
        ))}
      </ul>
      <div className="flex gap-1">
        <Input value={nouvelle} onChange={(e) => setNouvelle(e.target.value)} placeholder="Autre pièce attendue" className="rounded-none h-8 text-xs" />
        <Button size="sm" variant="outline" className="rounded-none h-8" disabled={nouvelle.trim().length < 2}
          onClick={() => { onDoe(lot.lot_code, { ajouter: nouvelle.trim() }); setNouvelle(''); }} aria-label="Ajouter la pièce">
          <Plus size={12} />
        </Button>
      </div>
      {!lot.doe.complet && (
        <Button size="sm" variant="outline" className="rounded-none gap-1 h-8" onClick={() => onRelancer(lot.lot_code)} data-testid={`doe-relancer-${lot.lot_code}`}>
          <PaperPlaneTilt size={12} /> Relancer l'entreprise
        </Button>
      )}
      {derniere && <p className={`text-xs ${derniere.envoye ? 'text-[#6B6B6B]' : 'text-[#8B3A3A]'}`} data-testid={`doe-relance-etat-${lot.lot_code}`}>{derniere.texte}</p>}
    </div>
  );
}
